import { useState, useMemo, useEffect } from 'react';
import { Button, useRecordContext, useNotify, useRefresh, useGetList } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { ChipList } from '../common';
import { useApiClient, useApiMutation } from '../../utils/hooks';

interface AddMachineToGroupButtonProps {
  onSuccess?: () => void;
}

export const AddMachineToGroupButton = ({ onSuccess }: AddMachineToGroupButtonProps) => {
  const [open, setOpen] = useState(false);
  const [selectedMachineIds, setSelectedMachineIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fetchWithAuth = useApiClient();

  // Get all machines
  const {
    data: machines,
    isLoading: machinesLoading,
    refetch,
  } = useGetList(
    'machines',
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'serial', order: 'ASC' },
      filter: {},
    },
    { enabled: open }
  );

  // Get current group's machines
  const [currentMachineIds, setCurrentMachineIds] = useState<number[]>([]);

  // Store machine groups info
  const [machineGroups, setMachineGroups] = useState<Record<number, string[]>>({});
  const [loadingGroups, setLoadingGroups] = useState(false);
  // Track whether data has been fetched (separate from currentMachineIds which can be empty)
  const [dataFetched, setDataFetched] = useState(false);

  const { mutate, loading } = useApiMutation();

  const handleOpen = () => {
    setOpen(true);
    setCurrentMachineIds([]); // Reset to force refetch
    setMachineGroups({}); // Clear machine groups data
    setLoadingGroups(false); // Reset loading state
    setDataFetched(false); // Reset data fetched flag
  };

  // Load group machines and machine groups when dialog opens and machines are loaded
  useEffect(() => {
    if (
      open &&
      record?.id &&
      machines &&
      Array.isArray(machines) &&
      machines.length > 0 &&
      !loadingGroups &&
      !dataFetched
    ) {
      setLoadingGroups(true);
      // Fetch current group's machines and all machine groups in parallel
      Promise.all([
        fetchWithAuth(`/api/groups/${record.id}/machines`),
        ...machines.map((machine) =>
          fetchWithAuth(`/api/machines/${machine.id}/groups`).catch(() => null)
        ),
      ])
        .then(async ([groupMachinesResponse, ...machineGroupsResponses]) => {
          const groupMachines = await groupMachinesResponse.json();
          setCurrentMachineIds(groupMachines.map((m: { id: number }) => m.id));

          // Process machine groups
          const groupsData: Record<number, string[]> = {};
          for (let i = 0; i < machines.length; i++) {
            const response = machineGroupsResponses[i];
            if (response && response.ok) {
              const groups = await response.json();
              groupsData[machines[i].id] = groups.map((g: { name: string }) => g.name);
            } else {
              groupsData[machines[i].id] = [];
            }
          }
          setMachineGroups(groupsData);
          setDataFetched(true);
        })
        .catch(() => {
          notify('Error loading group machines', { type: 'error' });
          setDataFetched(true); // Mark as fetched even on error to prevent retry loop
        })
        .finally(() => {
          setLoadingGroups(false);
        });
    }
  }, [open, record?.id, machines, loadingGroups, dataFetched, fetchWithAuth, notify]);

  const handleClose = () => {
    setOpen(false);
    setSelectedMachineIds([]);
  };

  const toggleMachineSelection = (machineId: number) => {
    setSelectedMachineIds((prev) =>
      prev.includes(machineId) ? prev.filter((id) => id !== machineId) : [...prev, machineId]
    );
  };

  const handleAddMachines = async () => {
    if (selectedMachineIds.length === 0 || !record?.id) {
      notify('Please select at least one machine', { type: 'warning' });
      return;
    }

    const errors: string[] = [];
    let successCount = 0;

    for (const machineId of selectedMachineIds) {
      try {
        await mutate(`/api/groups/${record.id}/machines/${machineId}`);
        successCount++;
      } catch {
        errors.push(`Error associating machine ${machineId}`);
      }
    }

    if (successCount > 0) {
      notify(`${successCount} machine${successCount > 1 ? 's' : ''} associated successfully`, {
        type: 'success',
      });
      refresh();
      if (onSuccess) {
        onSuccess();
      }
    }

    if (errors.length > 0) {
      notify(`${errors.length} error${errors.length > 1 ? 's' : ''} occurred`, { type: 'error' });
    }

    setSelectedMachineIds([]);
    refetch();
    // Close dialog if no errors
    if (errors.length === 0) {
      setOpen(false);
    }
  };

  // Filter machines that are not already associated with this group and match search query
  const availableMachines = useMemo(() => {
    const filtered = machines?.filter((machine) => !currentMachineIds.includes(machine.id)) || [];

    if (!searchQuery.trim()) {
      return filtered;
    }

    const query = searchQuery.toLowerCase();
    return filtered.filter((machine) => machine.serial.toLowerCase().includes(query));
  }, [machines, currentMachineIds, searchQuery]);

  if (!record) return null;

  return (
    <>
      <Button label="Add Machine" onClick={handleOpen} startIcon={<AddIcon />} />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Typography variant={isMobile ? 'h6' : 'h5'} component="div">
            Add Machines to {record.name}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 3 }, py: 2 }}>
          {machinesLoading || loadingGroups ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : availableMachines.length === 0 && !searchQuery.trim() ? (
            <Box sx={{ p: 2 }}>
              <Typography color="text.secondary" textAlign="center">
                No machines available to add. All machines are already associated with this group.
              </Typography>
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                placeholder="Search by serial..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              {availableMachines.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography color="text.secondary" textAlign="center">
                    No machines found matching &quot;{searchQuery}&quot;
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, px: 1 }}>
                    {availableMachines.length} machine{availableMachines.length !== 1 ? 's' : ''}{' '}
                    available
                  </Typography>
                  <List
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      maxHeight: '500px',
                      overflow: 'auto',
                    }}
                  >
                    {availableMachines.map((machine) => (
                      <ListItem
                        key={machine.id}
                        disablePadding
                        sx={{
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                      >
                        <ListItemButton
                          onClick={() => toggleMachineSelection(machine.id)}
                          selected={selectedMachineIds.includes(machine.id)}
                          sx={{
                            py: { xs: 1, sm: 1.25 },
                            px: { xs: 1.5, sm: 2 },
                            minHeight: { xs: 48, sm: 56 },
                            '&.Mui-selected': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(144, 202, 249, 0.16)'
                                  : 'rgba(25, 118, 210, 0.12)',
                              borderLeft: '4px solid',
                              borderLeftColor: 'primary.main',
                              '&:hover': {
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(144, 202, 249, 0.24)'
                                    : 'rgba(25, 118, 210, 0.18)',
                              },
                            },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={selectedMachineIds.includes(machine.id) ? 600 : 400}
                              sx={{ mb: machineGroups[machine.id]?.length > 0 ? 0.5 : 0 }}
                            >
                              {machine.serial}
                            </Typography>
                            {machineGroups[machine.id]?.length > 0 && (
                              <ChipList
                                items={machineGroups[machine.id].map((name, idx) => ({
                                  id: idx,
                                  label: name,
                                }))}
                                emptyText=""
                              />
                            )}
                          </Box>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, px: 1, display: 'block' }}
                  >
                    {selectedMachineIds.length} machine{selectedMachineIds.length !== 1 ? 's' : ''}{' '}
                    selected
                  </Typography>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, gap: 1 }}>
          <Button
            label="Cancel"
            onClick={handleClose}
            variant="outlined"
            startIcon={<CloseIcon />}
          />
          <Button
            label={`Add ${selectedMachineIds.length > 0 ? selectedMachineIds.length : ''} Machine${selectedMachineIds.length !== 1 ? 's' : ''}`}
            onClick={handleAddMachines}
            variant="contained"
            disabled={selectedMachineIds.length === 0 || loading || machinesLoading}
            startIcon={<AddIcon />}
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
